import React, { useState, useMemo, useCallback } from 'react';
import { cn, toDisplayString } from '@/lib/utils';
import { getCellValue, compareValues } from '@/lib/table-helpers';
import { renderFilterInput, renderCellContent } from '@/lib/table-ui-helpers';
import { Spinner } from './Spinner';
import { Button } from './Button';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from './icons';
import type { ColumnDef } from './DataTable';

// ============================================================================
// Type Definitions
// ============================================================================

export interface TreeNode<T> {
  data: T;
  children?: TreeNode<T>[];
}

export interface TreeDataTableProps<T> {
  nodes: TreeNode<T>[];
  columns: ColumnDef<T>[];
  keyExtractor: (row: T) => string | number;
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  enablePagination?: boolean;
  enableFilters?: boolean;
  defaultExpandedKeys?: Set<string | number>;
  className?: string;
}

interface FlatRow<T> {
  data: T;
  depth: number;
  hasChildren: boolean;
  key: string | number;
}

interface SortState {
  columnId: string | null;
  direction: 'asc' | 'desc';
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Check whether a single node's data matches ALL active filters. */
function rowMatchesFilters<T>(
  data: T,
  columns: ColumnDef<T>[],
  filters: Record<string, string>
): boolean {
  return columns.every((col) => {
    if (!col) return true;
    const filterValue = filters[col.id];
    if (!filterValue || !col.filter) return true;

    const cellValue = getCellValue(data, col.accessor);
    const cellStr = toDisplayString(cellValue).toLowerCase();
    const filterStr = filterValue.toLowerCase();

    if (col.filter.type === 'text') {
      return cellStr.includes(filterStr);
    }
    if (col.filter.type === 'select') {
      return toDisplayString(cellValue) === filterValue;
    }
    return true;
  });
}

/**
 * Tree-aware filter: returns a filtered copy of the tree.
 * - If a parent matches, it and ALL its children are included.
 * - If a child matches, its parent is included (even if the parent doesn't match).
 * - Returns null for nodes that neither match nor have matching descendants.
 */
function filterTree<T>(
  nodes: TreeNode<T>[],
  columns: ColumnDef<T>[],
  filters: Record<string, string>
): TreeNode<T>[] {
  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  if (!hasActiveFilters) return nodes;

  const result: TreeNode<T>[] = [];

  for (const node of nodes) {
    const selfMatches = rowMatchesFilters(node.data, columns, filters);

    if (selfMatches) {
      // Parent matches → include it with ALL children (unfiltered)
      result.push(node);
    } else if (node.children && node.children.length > 0) {
      // Parent doesn't match → check children recursively
      const filteredChildren = filterTree(node.children, columns, filters);
      if (filteredChildren.length > 0) {
        // At least one descendant matches → include parent with filtered children
        result.push({ data: node.data, children: filteredChildren });
      }
    }
    // else: no match, no matching descendants → exclude
  }

  return result;
}

/** Sort siblings within each level of the tree. */
function sortTree<T>(
  nodes: TreeNode<T>[],
  column: ColumnDef<T>,
  direction: 'asc' | 'desc'
): TreeNode<T>[] {
  const sorted = [...nodes].sort((a, b) => {
    const aVal = getCellValue(a.data, column.accessor);
    const bVal = getCellValue(b.data, column.accessor);
    return compareValues(aVal, bVal, direction);
  });

  return sorted.map((node) => {
    if (node.children && node.children.length > 0) {
      return { ...node, children: sortTree(node.children, column, direction) };
    }
    return node;
  });
}

/** Flatten a tree into visible rows based on expand state. */
function flattenTree<T>(
  nodes: TreeNode<T>[],
  keyExtractor: (row: T) => string | number,
  expandedKeys: Set<string | number>,
  depth: number = 0
): FlatRow<T>[] {
  const rows: FlatRow<T>[] = [];

  for (const node of nodes) {
    const key = keyExtractor(node.data);
    const hasChildren = !!(node.children && node.children.length > 0);
    rows.push({ data: node.data, depth, hasChildren, key });

    if (hasChildren && expandedKeys.has(key) && node.children) {
      const childRows = flattenTree(node.children, keyExtractor, expandedKeys, depth + 1);
      rows.push(...childRows);
    }
  }

  return rows;
}

// ============================================================================
// TreeDataTable Component
// ============================================================================

function TreeDataTableInner<T>(
  {
    nodes,
    columns,
    keyExtractor,
    isLoading = false,
    error = null,
    emptyMessage = 'No data found.',
    pageSizeOptions = [10, 25, 50],
    defaultPageSize = 10,
    enablePagination = true,
    enableFilters = true,
    defaultExpandedKeys,
    className,
  }: TreeDataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  // State
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(
    () => defaultExpandedKeys ?? new Set()
  );
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState>({ columnId: null, direction: 'asc' });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Toggle expand/collapse
  const toggleExpand = useCallback((key: string | number) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Filter tree
  const filteredNodes = useMemo(() => {
    return filterTree(nodes, columns, filters);
  }, [nodes, columns, filters]);

  // Sort tree
  const sortedNodes = useMemo(() => {
    if (!sort.columnId) return filteredNodes;
    const column = columns.find((col) => col.id === sort.columnId);
    if (!column) return filteredNodes;
    return sortTree(filteredNodes, column, sort.direction);
  }, [filteredNodes, columns, sort]);

  // Flatten into visible rows
  const flatRows = useMemo(() => {
    return flattenTree(sortedNodes, keyExtractor, expandedKeys);
  }, [sortedNodes, keyExtractor, expandedKeys]);

  // Paginate
  const paginatedRows = useMemo(() => {
    if (!enablePagination) return flatRows;
    const start = pagination.pageIndex * pagination.pageSize;
    return flatRows.slice(start, start + pagination.pageSize);
  }, [flatRows, pagination, enablePagination]);

  // Pagination calculations
  const totalRows = flatRows.length;
  const totalPages = Math.ceil(totalRows / pagination.pageSize);
  const canPreviousPage = pagination.pageIndex > 0;
  const canNextPage = pagination.pageIndex < totalPages - 1;

  // Handlers
  const handleFilterChange = (columnId: string, value: string) => {
    setFilters((prev) => ({ ...prev, [columnId]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleSort = (columnId: string) => {
    setSort((prev) => {
      if (prev.columnId === columnId) {
        return { columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { columnId, direction: 'asc' };
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPagination({ pageIndex: 0, pageSize: newSize });
  };

  const handlePreviousPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
  };

  const handleNextPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
  };

  // Render sort icon
  const renderSortIcon = (column: ColumnDef<T>) => {
    if (!column.sortable) return null;

    if (sort.columnId === column.id) {
      return sort.direction === 'asc' ? (
        <ChevronUpIcon className="h-4 w-4" />
      ) : (
        <ChevronDownIcon className="h-4 w-4" />
      );
    }
    return <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground/50" />;
  };

  // Alignment classes
  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div ref={ref} className={cn('flex items-center justify-center py-12', className)}>
        <Spinner size="lg" />
        <span className="ml-3 text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div ref={ref} className={cn('rounded-lg border border-error-200 bg-error-50 p-4', className)}>
        <p className="text-sm font-medium text-error-700">Error: {error}</p>
      </div>
    );
  }

  // Empty state (no data at all)
  if (!nodes || nodes.length === 0) {
    return (
      <div ref={ref} className={cn('py-12 text-center', className)}>
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  const hasFilters = enableFilters && columns.some((col) => col.filter);

  return (
    <div ref={ref} className={cn('w-full', className)}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full border-collapse">
          <thead>
            {/* Header row */}
            <tr className="bg-muted/50 border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    'px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider',
                    getAlignmentClass(column.align),
                    column.width
                  )}
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column.id)}
                      className={cn(
                        'inline-flex items-center gap-1 hover:text-foreground transition-colors',
                        sort.columnId === column.id && 'text-foreground'
                      )}
                    >
                      {column.header}
                      {renderSortIcon(column)}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>

            {/* Filter row */}
            {enableFilters && hasFilters && (
              <tr className="bg-muted/30 border-b border-border">
                {columns.map((column) => (
                  <th key={`filter-${column.id}`} className="px-3 py-1.5">
                    {column.filter
                      ? renderFilterInput(column.filter, column.id, filters, handleFilterChange)
                      : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {paginatedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                  No matching results found.
                </td>
              </tr>
            ) : (
              paginatedRows.map((flatRow) => (
                <tr
                  key={flatRow.key}
                  className="border-b border-border hover:bg-muted/30 transition-colors"
                >
                  {columns.map((column, colIndex) => {
                    const value = getCellValue(flatRow.data, column.accessor);
                    const isFirstColumn = colIndex === 0;

                    return (
                      <td
                        key={column.id}
                        className={cn(
                          'px-3 py-1.5 text-sm text-foreground',
                          getAlignmentClass(column.align),
                          column.width
                        )}
                        style={isFirstColumn ? { paddingLeft: `${12 + flatRow.depth * 24}px` } : undefined}
                      >
                        {isFirstColumn ? (
                          <span className="inline-flex items-center gap-1">
                            {flatRow.hasChildren ? (
                              <button
                                type="button"
                                onClick={() => toggleExpand(flatRow.key)}
                                className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-muted/50 transition-colors flex-shrink-0"
                                aria-label={expandedKeys.has(flatRow.key) ? 'Collapse row' : 'Expand row'}
                              >
                                {expandedKeys.has(flatRow.key) ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <span className="inline-block w-5 flex-shrink-0" />
                            )}
                            <span>{renderCellContent(column, value, flatRow.data)}</span>
                          </span>
                        ) : (
                          renderCellContent(column, value, flatRow.data)
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-background">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <select
              value={pagination.pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {totalRows === 0
                ? '0 results'
                : `${pagination.pageIndex * pagination.pageSize + 1}-${Math.min(
                    (pagination.pageIndex + 1) * pagination.pageSize,
                    totalRows
                  )} of ${totalRows}`}
            </span>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={!canPreviousPage}
                aria-label="Previous page"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!canNextPage}
                aria-label="Next page"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ForwardRef wrapper with generic support
export const TreeDataTable = React.forwardRef(TreeDataTableInner) as <T>(
  props: TreeDataTableProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;
